import React, { useState, useEffect, useCallback } from "react";
import { Select, Input, Button, Badge, Tooltip, Table, Modal } from "antd";
import {
  Copy,
  Trash2,
  BarChart3,
  Plus,
  GripVertical,
  Bolt,
} from "lucide-react";
import PlusMinQty from "../../../utilities/PlusMinQty";

const { Option } = Select;

const StrategyLegs = ({
  tradingLegs,
  symbol,
  multiplier,
  updateMultiplier,
  handleTradingLegChange,
  addTradingLeg,
  copyTradingLeg,
  removeTradingLeg,
  reorderLegs,
  absoluteExpiryOptions,
  atmStrike,
  strikeListCache,
  fetchStrikeList,
  legErrorMessages,
  strategyList,
  isEditMode,
  strategyId,
}) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Risk Modal State
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [riskModalLegId, setRiskModalLegId] = useState(null);
  const [riskForm, setRiskForm] = useState({
    stopLossAbsolute: null,
    trailSL: { reference: "", step: "", valueType: 2 },
    profitLockAt: "",
    profitLockSl: "",
    profitLockTrail: { reference: "", step: "", valueType: 2 },
  });
  const [riskTrailValueType, setRiskTrailValueType] = useState(2);
  const [riskIsPNL, setRiskIsPNL] = useState(false);
  const [riskErrorMessages, setRiskErrorMessages] = useState({
    trailSL: "",
    profitLock: "",
    profitLockTrail: "",
  });

  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => "₹₹";
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");

  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };

  const onClickLegSlType = (legId) => {
    const leg = tradingLegs.find((l) => l.id === legId);
    if (!leg) return;

    const basis = (leg?.rms?.basis ?? 0) === 0 ? "PNL" : "Price";
    if (basis === "PNL") {
      const newType = cycleWithin(leg.rms.stopLossInput.valueType, [0, 2]);
      handleTradingLegChange(legId, "rms.stopLossInput.valueType", newType);
      return;
    }
    const currentType = leg.rms.stopLossInput.valueType;
    const newType = cycleWithin(currentType, [1, 2]);
    handleTradingLegChange(legId, "rms.stopLossInput.valueType", newType);
  };

  const onClickLegTpType = (legId) => {
    const leg = tradingLegs.find((l) => l.id === legId);
    if (!leg) return;

    const basis = (leg?.rms?.basis ?? 0) === 0 ? "PNL" : "Price";
    if (basis === "PNL") {
      const newType = cycleWithin(leg.rms.takeProfitInput.valueType, [0, 2]);
      handleTradingLegChange(legId, "rms.takeProfitInput.valueType", newType);
      return;
    }
    const currentType = leg.rms.takeProfitInput.valueType;
    const newType = cycleWithin(currentType, [1, 2]);
    handleTradingLegChange(legId, "rms.takeProfitInput.valueType", newType);
  };


  const openRiskModal = useCallback((leg) => {
    setRiskModalLegId(leg.id);
    const trailValueType = leg?.rms?.trailSL?.valueType ?? 2;
    const isPNL = (leg?.rms?.basis ?? 0) === 0;
    setRiskTrailValueType(trailValueType);
    setRiskIsPNL(isPNL);
    setRiskForm({
      stopLossAbsolute: leg?.rms?.stopLossInput?.value || null,
      trailSL: {
        reference: leg?.rms?.trailSL?.reference || "",
        step: leg?.rms?.trailSL?.step || "",
        valueType: trailValueType,
      },
      profitLockAt: leg?.rms?.profitLockAt || "",
      profitLockSl: leg?.rms?.profitLockSl || "",
      profitLockTrail: {
        reference: leg?.rms?.profitLockTrail?.reference || "",
        step: leg?.rms?.profitLockTrail?.step || "",
        valueType: 2,
      },
    });
    setRiskErrorMessages({ trailSL: "", profitLock: "", profitLockTrail: "" });
    setIsRiskModalOpen(true);
  }, []);

  const onClickRiskTrailType = () => {
    const currentType = riskForm.trailSL.valueType;
    const newType = cycleWithin(currentType, [2, 1]);
    setRiskTrailValueType(newType);
    setRiskForm((prev) => ({
      ...prev,
      trailSL: { ...prev.trailSL, valueType: newType },
    }));
  };

  const validateRiskTrailSL = (ref, step) => {
    const refVal =
      ref !== null && ref !== undefined && ref !== "" ? parseFloat(ref) : null;
    const stepVal =
      step !== null && step !== undefined && step !== "" ? parseFloat(step) : null;

    if (
      (refVal !== null && stepVal === null) ||
      (refVal === null && stepVal !== null)
    ) {
      setRiskErrorMessages((prev) => ({
        ...prev,
        trailSL: "Both fields are required",
      }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setRiskErrorMessages((prev) => ({
          ...prev,
          trailSL: "Trail SL Reference and Step must be non-negative",
        }));
        return false;
      }
    }

    setRiskErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateRiskProfitLock = (profitLockAt, profitLockSl) => {
    const atVal =
      profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== ""
        ? parseFloat(profitLockAt)
        : null;
    const slVal =
      profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== ""
        ? parseFloat(profitLockSl)
        : null;

    if (atVal !== null && slVal === null) {
      setRiskErrorMessages((prev) => ({
        ...prev,
        profitLock: "Both fields are required",
      }));
      return false;
    }

    setRiskErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateRiskProfitLockTrail = (ref, step) => {
    const refVal =
      ref !== null && ref !== undefined && ref !== "" ? parseFloat(ref) : null;
    const stepVal =
      step !== null && step !== undefined && step !== "" ? parseFloat(step) : null;

    if (
      (refVal !== null && stepVal === null) ||
      (refVal === null && stepVal !== null)
    ) {
      setRiskErrorMessages((prev) => ({
        ...prev,
        profitLockTrail: "Both fields are required",
      }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setRiskErrorMessages((prev) => ({
          ...prev,
          profitLockTrail: "Profit Trail values cannot be negative",
        }));
        return false;
      }
    }

    setRiskErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };

  useEffect(() => {
    if (!isRiskModalOpen || !riskForm || Object.keys(riskForm).length === 0)
      return;

    validateRiskTrailSL(riskForm.trailSL?.reference, riskForm.trailSL?.step);
    validateRiskProfitLock(riskForm.profitLockAt, riskForm.profitLockSl);
    validateRiskProfitLockTrail(
      riskForm.profitLockTrail?.reference,
      riskForm.profitLockTrail?.step
    );
  }, [isRiskModalOpen, riskForm]);

  const handleRiskSave = () => {
    if (riskModalLegId == null) {
      setIsRiskModalOpen(false);
      return;
    }

    if (
      !validateRiskTrailSL(riskForm.trailSL?.reference, riskForm.trailSL?.step)
    ) {
      return;
    }

    if (!validateRiskProfitLock(riskForm.profitLockAt, riskForm.profitLockSl)) {
      return;
    }

    if (
      !validateRiskProfitLockTrail(
        riskForm.profitLockTrail?.reference,
        riskForm.profitLockTrail?.step
      )
    ) {
      return;
    }

    const id = riskModalLegId;
    handleTradingLegChange(
      id,
      "rms.trailSL.reference",
      riskForm.trailSL.reference
    );
    handleTradingLegChange(id, "rms.trailSL.step", riskForm.trailSL.step);
    handleTradingLegChange(id, "rms.trailSL.valueType", riskForm.trailSL.valueType);

    handleTradingLegChange(id, "rms.profitLockAt", riskForm.profitLockAt || "");
    handleTradingLegChange(id, "rms.profitLockSl", riskForm.profitLockSl || "");

    handleTradingLegChange(
      id,
      "rms.profitLockTrail.reference",
      riskForm.profitLockTrail.reference
    );
    handleTradingLegChange(
      id,
      "rms.profitLockTrail.step",
      riskForm.profitLockTrail.step
    );
    handleTradingLegChange(
      id,
      "rms.profitLockTrail.valueType",
      2
    );

    setIsRiskModalOpen(false);
  };

  return (
    <div className="config-section">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <div className="qt-algo-section-header">
          <BarChart3 size={20} />
          <h3>Strategy Legs</h3>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifySelf: "flex-end",
          }}
        >
          <label style={{ fontWeight: 500 }}>Qty Multiplier</label>
          <PlusMinQty
            qtyClick={(qty, value) => {
              if (parseFloat(qty) > 0) {
                updateMultiplier(parseFloat(qty));
              }
            }}
            filterValue={multiplier}
            name={"multiplier"}
            min={1}
            step={1}
          />
        </div>
      </div>
      <Table
        rowKey="id"
        dataSource={tradingLegs}
        columns={[
          {
            title: "",
            key: "drag",
            width: 36,
            align: "center",
            render: (_, __, index) => (
              <span
                draggable
                onDragStart={(e) => {
                  try {
                    e.dataTransfer.setData("text/plain", String(index));
                  } catch { }
                  try {
                    e.dataTransfer.effectAllowed = "move";
                  } catch { }
                  setDragIndex(index);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: dragIndex === index ? "#3b82f6" : "#9ca3af",
                  cursor: "grab",
                  userSelect: "none",
                  transition: "color 0.2s ease",
                }}
              >
                <GripVertical size={14} />
              </span>
            ),
          },
          {
            title: "Leg ID",
            dataIndex: "id",
            key: "id",
            width: 80,
            align: "center",
            render: (id) => (
              <Badge count={id} style={{ backgroundColor: "#3b82f6" }} />
            ),
          },
          {
            title: "Idle",
            dataIndex: "idle",
            key: "idle",
            width: 80,
            align: "center",
            render: (checked, record) =>
              record.onTgtAction !== "Do Nothing" ? (
                <Tooltip overlayStyle={{ position: 'fixed' }} title="Feature coming soon">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled
                    onChange={(e) =>
                      handleTradingLegChange(record.id, "idle", e.target.checked)
                    }
                    style={{ cursor: "not-allowed" }}
                  />
                </Tooltip>
              ) : (
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    handleTradingLegChange(record.id, "idle", e.target.checked)
                  }
                />
              ),
          },
          {
            title: "Side",
            dataIndex: "side",
            key: "side",
            align: "center",
            width: 80,
            render: (value, record) => (
              <Button
                size="small"
                onClick={() =>
                  handleTradingLegChange(
                    record.id,
                    "side",
                    value === "BUY" ? "SELL" : "BUY"
                  )
                }
                style={{
                  width: "100%",
                  backgroundColor: value === "BUY" ? "#16a34a" : "#ef4444",
                  color: "#ffffff",
                  border: "none",
                }}
              >
                {value}
              </Button>
            ),
          },
          {
            title: "Fut/Opt",
            dataIndex: "futOpt",
            key: "futOpt",
            width: 80,
            align: "center",
            render: (value, record) => (
              <Button
                size="small"
                onClick={() =>
                  handleTradingLegChange(
                    record.id,
                    "futOpt",
                    value === "FUT" ? "OPT" : "FUT"
                  )
                }
                className="fut-opt-button"
              >
                {value}
              </Button>
            ),
          },
          {
            title: "Type",
            dataIndex: "optionType",
            key: "optionType",
            width: 80,
            align: "center",
            render: (value, record) =>
              record.futOpt === "OPT" ? (
                <Button
                  size="small"
                  onClick={() =>
                    handleTradingLegChange(
                      record.id,
                      "optionType",
                      value === "PE" ? "CE" : "PE"
                    )
                  }
                  className="option-type-button"
                >
                  {value || "CE"}
                </Button>
              ) : (
                <span style={{ color: "#6b7280" }}> - </span>
              ),
          },
          {
            title: "Strike Selection",
            dataIndex: "strikeSelection",
            key: "strikeSelection",
            width: 130,
            align: "center",
            render: (value, record) =>
              record.futOpt === "FUT" ? (
                <span style={{ color: "#6b7280" }}> - </span>
              ) : (
                <Select
                  value={value}
                  style={{ width: "100%" }}
                  onChange={(val) =>
                    handleTradingLegChange(record.id, "strikeSelection", val)
                  }
                >
                  <Option value="Prem">Premium (LTP)</Option>
                  <Option value="Delta">Delta</Option>
                  <Option value="Distance">Distance</Option>
                  <Option value="Absolute">Absolute</Option>
                </Select>
              ),
          },
          {
            title: "Strike Value",
            dataIndex: "strikeValue",
            key: "strikeValue",
            width: 120,
            align: "center",
            render: (value, record) =>
              record.futOpt === "FUT" ? (
                <span style={{ color: "#6b7280" }}> - </span>
              ) : record.strikeSelection === "Distance" ? (
                (() => {
                  const distanceOptions = [];
                  for (let i = -40; i <= 40; i += 1) {
                    const label =
                      i === 0 ? "ATM" : i > 0 ? `OTM${i}` : `ITM${Math.abs(i)}`;
                    distanceOptions.push({ value: i, label });
                  }
                  const parsedValue =
                    value === "" || value === null || value === undefined
                      ? undefined
                      : Number(value);
                  return (
                    <Select
                      showSearch
                      placeholder="Select distance"
                      value={parsedValue}
                      onChange={(val) =>
                        handleTradingLegChange(record.id, "strikeValue", val)
                      }
                      filterOption={(input, option) =>
                        (option?.children || "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      style={{ width: "100%" }}
                      size={"middle"}
                      onDropdownVisibleChange={(open) => {
                        if (!open) return;

                        setTimeout(() => {
                          const atmIndex = distanceOptions.findIndex(
                            (opt) => opt.value === 0
                          );
                          if (atmIndex === -1) return;

                          const dropdown = document.querySelector(
                            ".ant-select-dropdown:not(.ant-select-dropdown-hidden)"
                          );
                          if (!dropdown) return;

                          const holder = dropdown.querySelector(
                            ".rc-virtual-list-holder"
                          );
                          if (!holder) return;

                          const firstOption = holder.querySelector(
                            ".ant-select-item-option"
                          );
                          const optionHeight = firstOption?.offsetHeight || 32;

                          const targetScrollTop = Math.max(
                            atmIndex * optionHeight - holder.clientHeight / 2,
                            0
                          );

                          holder.scrollTop = targetScrollTop;
                        }, 80);
                      }}
                    >
                      {distanceOptions.map((opt) => (
                        <Option
                          key={opt.value}
                          value={opt.value}
                          title={opt.label}
                          style={{
                            backgroundColor:
                              opt.value === 0 ? "#e6f4ff" : "transparent",
                            fontWeight: opt.value === 0 ? 600 : 400,
                          }}
                        >
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  );
                })()
              ) : (
                (() => {
                  if (record.strikeSelection === "Absolute") {
                    const cacheKey = symbol;
                    const options = strikeListCache[cacheKey] || [];
                    if (!options.length && symbol) {
                      fetchStrikeList(symbol);
                    }
                    const parsedVal =
                      value === "" || value === null || value === undefined
                        ? undefined
                        : Number(value);
                    return (
                      <Select
                        showSearch
                        placeholder={
                          options.length ? "Select strike" : "Loading strikes..."
                        }
                        value={parsedVal}
                        onChange={(val) =>
                          handleTradingLegChange(record.id, "strikeValue", val)
                        }
                        filterOption={(input, option) =>
                          (option?.children || "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ width: "100%" }}
                        size={"middle"}
                        onDropdownVisibleChange={(open) => {
                          if (!open || !atmStrike) return;

                          setTimeout(() => {
                            const index = options.findIndex(
                              (st) => st === atmStrike
                            );
                            if (index === -1) return;

                            const dropdown = document.querySelector(
                              ".ant-select-dropdown:not(.ant-select-dropdown-hidden)"
                            );
                            if (!dropdown) return;

                            const holder = dropdown.querySelector(
                              ".rc-virtual-list-holder"
                            );
                            if (!holder) return;

                            const firstOption = holder.querySelector(
                              ".ant-select-item-option"
                            );
                            const optionHeight = firstOption?.offsetHeight || 32;

                            const targetScrollTop = Math.max(
                              index * optionHeight - holder.clientHeight / 2,
                              0
                            );

                            holder.scrollTop = targetScrollTop;
                          }, 0);
                        }}
                      >
                        {options.map((st) => (
                          <Option
                            key={st}
                            value={st}
                            style={{
                              backgroundColor:
                                st === atmStrike ? "#e6f4ff" : "transparent",
                              fontWeight: st === atmStrike ? 600 : 400,
                            }}
                          >
                            {st}
                          </Option>
                        ))}
                      </Select>
                    );
                  }
                  return (
                    <Input
                      type="number"
                      placeholder="Value"
                      value={value}
                      onChange={(e) =>
                        handleTradingLegChange(
                          record.id,
                          "strikeValue",
                          e.target.value
                        )
                      }
                    />
                  );
                })()
              ),
          },
          {
            title: "Expiry Selection",
            dataIndex: "expirySelection",
            key: "expirySelection",
            width: 130,
            align: "center",
            render: (value, record) => (
              <Select
                value={value}
                style={{ width: "100%" }}
                onChange={(val) =>
                  handleTradingLegChange(record.id, "expirySelection", val)
                }
              >
                <Option value="Absolute">Absolute</Option>
                <Option value="Relative">Relative</Option>
              </Select>
            ),
          },
          {
            title: "Expiry",
            dataIndex: "expiry",
            key: "expiry",
            width: 180,
            align: "center",
            render: (value, record) => {
              const weeklySupported = symbol === "NIFTY" || symbol === "SENSEX";
              const isAbsolute = record.expirySelection === "Absolute";
              if (isAbsolute) {
                return (
                  <Select
                    showSearch
                    value={value || undefined}
                    placeholder={
                      absoluteExpiryOptions.length ? "Select expiry" : "No expiries"
                    }
                    style={{ width: "100%" }}
                    onChange={(val) =>
                      handleTradingLegChange(record.id, "expiry", val)
                    }
                    filterOption={(input, option) =>
                      (option?.children || "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {absoluteExpiryOptions.map((dt) => (
                      <Option key={dt} value={dt}>
                        {dt}
                      </Option>
                    ))}
                  </Select>
                );
              }
              return (
                <Select
                  value={value}
                  style={{ width: "100%" }}
                  onChange={(val) =>
                    handleTradingLegChange(record.id, "expiry", val)
                  }
                >
                  {weeklySupported && (
                    <>
                      <Option value="Current Weekly">Current Weekly</Option>
                      <Option value="Next Weekly">Next Weekly</Option>
                    </>
                  )}
                  <Option value="Current Monthly">Current Monthly</Option>
                  <Option value="Next Monthly">Next Monthly</Option>
                </Select>
              );
            },
          },
          {
            title: "Qty (Lots)",
            key: "qtyLots",
            width: 100,
            align: "center",
            render: (_, record) => (
              <Tooltip
                overlayStyle={{ position: 'fixed' }}
                title={legErrorMessages[record.id]?.qty || null}
                open={legErrorMessages[record.id]?.qty}
              >
                <div
                  className={
                    legErrorMessages[record.id]?.qty ? "error-border" : ""
                  }
                >
                  <Input
                    type="number"
                    min="1"
                    value={record.qty}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleTradingLegChange(record.id, "qty", parseInt(val));
                    }}
                  />
                </div>
              </Tooltip>
            ),
          },
          {
            title: "SL/TP Basis",
            dataIndex: "slTpBasis",
            key: "slTpBasis",
            width: 130,
            align: "center",
            render: (value, record) => (
              <Select
                value={(record.rms.basis ?? 0) === 0 ? "PNL" : "Price"}
                style={{ width: "100%" }}
                onChange={(val) => {
                  handleTradingLegChange(record.id, "rms.basis", val === "PNL" ? 0 : 1);
                  if (val === "PNL") {
                    handleTradingLegChange(record.id, "rms.stopLossInput.valueType", 0);
                    handleTradingLegChange(record.id, "rms.takeProfitInput.valueType", 0);
                  }
                }}
              >
                <Option value="Price">Price</Option>
                <Option value="PNL">PNL</Option>
              </Select>
            ),
          },

          {
            title: "Stoploss value",
            dataIndex: "slValue",
            key: "slValue",
            width: 140,
            align: "center",
            render: (value, record) => (
              <Tooltip
                overlayStyle={{ position: 'fixed' }}
                title={legErrorMessages[record.id]?.stopLoss || null}
                open={legErrorMessages[record.id]?.stopLoss}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    className={
                      legErrorMessages[record.id]?.stopLoss ? "error-border" : ""
                    }
                  >
                    <PlusMinQty
                      qtyClick={(qty) =>
                        handleTradingLegChange(
                          record.id,
                          "rms.stopLossInput.value",
                          qty
                        )
                      }
                      filterValue={record.rms.stopLossInput.value || null}
                      name={"stopLossAbsolute"}
                      min={null}
                      step={0.05}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {(record?.rms?.basis ?? 0) === 0 ? (
                    <div
                      className="pts-class"
                      onClick={() => onClickLegSlType(record.id)}
                    >
                      {renderPNLSLTPLabel(record.rms.stopLossInput.valueType)}
                    </div>
                  ) : (
                    <div
                      className="pts-class"
                      onClick={() => onClickLegSlType(record.id)}
                    >
                      {renderTypeLabel(record.rms.stopLossInput.valueType)}
                    </div>
                  )}
                </div>
              </Tooltip>
            ),
          },
          {
            title: "Target value",
            dataIndex: "tpValue",
            key: "tpValue",
            width: 140,
            align: "center",
            render: (value, record) => (
              <Tooltip
                overlayStyle={{ position: 'fixed' }}
                title={legErrorMessages[record.id]?.target || null}
                open={legErrorMessages[record.id]?.target}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    className={
                      legErrorMessages[record.id]?.target ? "error-border" : ""
                    }
                  >
                    <PlusMinQty
                      qtyClick={(qty) =>
                        handleTradingLegChange(
                          record.id,
                          "rms.takeProfitInput.value",
                          qty
                        )
                      }
                      filterValue={record.rms.takeProfitInput.value || null}
                      name={"takeProfitAbsolute"}
                      min={null}
                      step={0.05}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {(record?.rms?.basis ?? 0) === 0 ? (
                    <div
                      className="pts-class"
                      onClick={() => onClickLegTpType(record.id)}
                    >
                      {renderPNLSLTPLabel(record.rms.takeProfitInput.valueType)}
                    </div>
                  ) : (
                    <div
                      className="pts-class"
                      onClick={() => onClickLegTpType(record.id)}
                    >
                      {renderTypeLabel(record.rms.takeProfitInput.valueType)}
                    </div>
                  )}
                </div>
              </Tooltip>
            ),
          },
          {
            title: "On SL Action",
            dataIndex: "onSlAction",
            key: "onSlAction",
            width: 320,
            align: "center",
            render: (value, record) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Select
                  value={Number(value) || 0}
                  style={{ width: "260px" }}
                  onChange={(val) => handleTradingLegChange(record.id, "onSlAction", val)}
                >
                  <Option value={0}>Default</Option>
                  <Option value={1}>Close + SL to cost (All legs)</Option>
                  <Option value={2}>Close + SL to cost (Profitable legs only)</Option>
                  <Option value={3}>Close Strategy</Option>
                  <Option value={4}>Close + Re-enter</Option>
                  <Option value={5}>Close + Open another strategy</Option>
                </Select>
                {Number(value) === 4 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>Max Re-entries: </span>
                    <Input
                      type="number"
                      size="middle"
                      min={1}
                      placeholder="Count"
                      value={record.onSlReEntryCount || 1}
                      onChange={(e) => handleTradingLegChange(record.id, "onSlReEntryCount", parseInt(e.target.value) || 1)}
                      style={{ width: '60px' }}
                    />
                  </div>
                )}
                {Number(value) === 5 && (
                  <Select
                    size="middle"
                    placeholder="Select Strategy"
                    showSearch
                    value={record.onSlTargetStrategyId || undefined}
                    style={{ width: '100%' }}
                    onChange={(val) => handleTradingLegChange(record.id, "onSlTargetStrategyId", val)}
                    filterOption={(input, option) =>
                      (option?.children || "").toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {(strategyList || []).filter(s => String(s.id) !== String(strategyId)).map(s => (
                      <Option key={s.id} value={s.id}>{s.name || s.id}</Option>
                    ))}
                  </Select>
                )}
              </div>
            ),
          },

          {
            title: "On TGT Action",
            dataIndex: "onTgtAction",
            key: "onTgtAction",
            width: 130,
            align: "center",
            render: (value, record) => (
              <Select value={"Exit Leg"} style={{ width: "100%" }} disabled>
                <Option value="Exit Leg">Exit Leg</Option>
                <Option value="Exit Strategy">Exit Strategy</Option>
                <Option value="Do Nothing">Do Nothing</Option>
              </Select>
            ),
          },
          {
            title: "Trailing SL",
            key: "trailingSl",
            width: 100,
            align: "center",
            render: (_, record) => (
              <div style={{ fontSize: "12px", textAlign: "center" }}>
                {record.rms.trailSL.reference && record.rms.trailSL.step ? (
                  `${record.rms.trailSL.reference}-${record.rms.trailSL.step}`
                ) : (
                  <span style={{ color: "#999" }}> - </span>
                )}
              </div>
            ),
          },
          {
            title: "Profit Locking & Trailing",
            key: "profitLockingTrailing",
            width: 140,
            align: "center",
            render: (_, record) => (
              <div style={{ fontSize: "12px", textAlign: "center" }}>
                {record.rms.profitLockAt &&
                  record.rms.profitLockSl &&
                  record.rms.profitLockTrail ? (
                  `${record.rms.profitLockAt}-${record.rms.profitLockSl}-${record.rms.profitLockTrail.reference}-${record.rms.profitLockTrail.step}`
                ) : (
                  <span style={{ color: "#999" }}> - </span>
                )}
              </div>
            ),
          },
          {
            title: "Risk Settings",
            key: "riskSettings",
            width: 60,
            align: "center",
            render: (_, record) => (
              <Button
                type="text"
                size="small"
                icon={<Bolt size={18} />}
                onClick={() => openRiskModal(record)}
              />
            ),
          },
          {
            title: "Actions",
            key: "actions",
            width: 120,
            align: "center",
            render: (_, record) => (
              <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                <Tooltip overlayStyle={{ position: 'fixed' }} title="Copy Leg">
                  <Button
                    type="text"
                    size="small"
                    icon={<Copy size={16} />}
                    onClick={() => copyTradingLeg(record.id)}
                  />
                </Tooltip>
                <Tooltip overlayStyle={{ position: 'fixed' }} title="Delete Leg">
                  <Button
                    type="text"
                    size="small"
                    icon={<Trash2 size={16} />}
                    style={{ color: "#ff4d4f" }}
                    onClick={() => removeTradingLeg(record.id)}
                    disabled={tradingLegs.length === 1}
                  />
                </Tooltip>
              </div>
            ),
          },
        ]}
        onRow={(record, index) => ({
          onDragOver: (e) => {
            e.preventDefault();
            try {
              e.dataTransfer.dropEffect = "move";
            } catch { }
            setDragOverIndex(index);
          },
          onDragLeave: () => {
            setDragOverIndex(null);
          },
          onDrop: (e) => {
            e.preventDefault();
            let from = dragIndex;
            try {
              const dt = e.dataTransfer.getData("text/plain");
              if (dt !== undefined && dt !== null && dt !== "")
                from = parseInt(dt, 10);
            } catch { }
            reorderLegs(from, index);
            setDragIndex(null);
            setDragOverIndex(null);
          },
          style: {
            opacity: dragIndex === index ? 0.5 : 1,
            backgroundColor:
              dragOverIndex === index && dragIndex !== index
                ? "#f0f9ff"
                : "transparent",
            borderTop:
              dragOverIndex === index && dragIndex !== index
                ? "2px solid #3b82f6"
                : "none",
            transition: "all 0.2s ease",
            cursor: dragIndex === index ? "grabbing" : "default",
          },
        })}
        pagination={false}
        scroll={{ x: 2120 }}
        size="small"
        style={{ marginBottom: 16 }}
      />
      {isEditMode && (
        <div style={{ marginBottom: 16 }} className="info-note">
          <strong>Note:</strong> Changes apply to new entries. Open positions (if any) will keep their current action until they close.
        </div>
      )}
      <Button
        type="primary"
        size="large"
        style={{ width: "100%" }}
        icon={<Plus size={16} />}
        onClick={addTradingLeg}
        className="btn-add-leg"
      >
        Add Strategy Leg
      </Button>

      <Modal
        title={`Risk Settings - Leg ${riskModalLegId ?? ""}`}
        open={isRiskModalOpen}
        onOk={handleRiskSave}
        onCancel={() => setIsRiskModalOpen(false)}
        width={400}
        okText="Save"
        cancelText="Cancel"
        okButtonProps={{
          disabled:
            riskErrorMessages.trailSL ||
            riskErrorMessages.profitLock ||
            riskErrorMessages.profitLockTrail,
        }}
      >
        <div className="risk-modal-container">
          <div className="risk-modal-section risk-modal-section-trailing">
            <div className="risk-modal-section-title">Trailing SL</div>
            <div className="risk-modal-input-row">
              <div className="risk-modal-input-container">
                <label className="risk-modal-label">For every X move</label>
                <PlusMinQty
                  qtyClick={(qty) => {
                    setRiskForm((prev) => ({
                      ...prev,
                      trailSL: { ...prev.trailSL, reference: qty },
                    }));
                    setTimeout(() => {
                      validateRiskTrailSL(qty, riskForm.trailSL.step);
                    }, 0);
                  }}
                  filterValue={riskForm.trailSL.reference || null}
                  name={"trailReferenceAbsolute"}
                  min={0.05}
                  step={0.05}
                  max={null}
                  title={""}
                  formatINR={true}
                  disabled={riskForm.stopLossAbsolute === null}
                />
              </div>
              <div className="risk-modal-input-container">
                <label className="risk-modal-label">Trail SL by Y</label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <PlusMinQty
                    qtyClick={(qty) => {
                      setRiskForm((prev) => ({
                        ...prev,
                        trailSL: { ...prev.trailSL, step: qty },
                      }));
                      setTimeout(() => {
                        validateRiskTrailSL(riskForm.trailSL.reference, qty);
                      }, 0);
                    }}
                    filterValue={riskForm.trailSL.step || null}
                    name={"trailStepAbsolute"}
                    min={0.05}
                    step={0.05}
                    max={null}
                    title={""}
                    formatINR={true}
                    disabled={riskForm.stopLossAbsolute === null}
                  />
                  {riskIsPNL ? (
                    <div className="pts-class risk-modal-pts-border">
                      {renderPNLLabel()}
                    </div>
                  ) : (
                    <div
                      className="pts-class risk-modal-pts-border"
                      onClick={onClickRiskTrailType}
                    >
                      {renderTrailTypeLabel(riskTrailValueType)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {riskErrorMessages.trailSL && (
              <div
                className="text-danger mt-1 mb-2"
                style={{ fontSize: "10px", textAlign: "center" }}
              >
                {riskErrorMessages.trailSL}
              </div>
            )}
          </div>
          <div className="risk-modal-section risk-modal-section-locking">
            <div className="risk-modal-section-title">Profit Locking</div>
            <div className="risk-modal-input-row">
              <div className="risk-modal-input-container">
                <label className="risk-modal-label">When profit reaches</label>
                <PlusMinQty
                  qtyClick={(qty) => {
                    setRiskForm((prev) => {
                      const updated = { ...prev, profitLockAt: qty };
                      if (!qty || qty === 0 || qty === null) {
                        updated.profitLockTrail = {
                          ...prev.profitLockTrail,
                          reference: "",
                          step: "",
                        };
                      }
                      return updated;
                    });
                    setTimeout(() => {
                      validateRiskProfitLock(qty, riskForm.profitLockSl);
                    }, 0);
                  }}
                  filterValue={riskForm.profitLockAt || null}
                  name={"profitLockAt"}
                  min={null}
                  step={0.05}
                  max={null}
                  title={""}
                  formatINR={true}
                />
              </div>
              <div className="risk-modal-input-container">
                <label className="risk-modal-label">Lock minimum profit at</label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <PlusMinQty
                    qtyClick={(qty) => {
                      setRiskForm((prev) => {
                        const updated = { ...prev, profitLockSl: qty };
                        if (!qty || qty === 0 || qty === null) {
                          updated.profitLockTrail = {
                            ...prev.profitLockTrail,
                            reference: "",
                            step: "",
                          };
                        }
                        return updated;
                      });
                      setTimeout(() => {
                        validateRiskProfitLock(riskForm.profitLockAt, qty);
                      }, 0);
                    }}
                    filterValue={riskForm.profitLockSl || null}
                    name={"profitLockSl"}
                    min={null}
                    step={0.05}
                    max={null}
                    title={""}
                    formatINR={true}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                </div>
              </div>
            </div>
            {riskErrorMessages.profitLock && (
              <div
                className="text-danger mt-1 mb-2"
                style={{ fontSize: "10px", textAlign: "center" }}
              >
                {riskErrorMessages.profitLock}
              </div>
            )}
          </div>
          <div className="risk-modal-section risk-modal-section-profit-trail">
            <div className="risk-modal-section-title">Profit Trailing</div>
            <div className="risk-modal-input-row">
              <div className="risk-modal-input-container">
                <label className="risk-modal-label">If profit increases by X</label>
                <PlusMinQty
                  qtyClick={(qty) => {
                    setRiskForm((prev) => ({
                      ...prev,
                      profitLockTrail: { ...prev.profitLockTrail, reference: qty },
                    }));
                    setTimeout(() => {
                      validateRiskProfitLockTrail(qty, riskForm.profitLockTrail.step);
                    }, 0);
                  }}
                  filterValue={riskForm.profitLockTrail.reference || null}
                  name={"profitLockTrailRef"}
                  min={0.05}
                  step={0.05}
                  max={null}
                  title={""}
                  formatINR={true}
                  disabled={
                    riskForm.profitLockAt === null ||
                    riskForm.profitLockAt === "" ||
                    riskForm.profitLockAt === 0 ||
                    riskForm.profitLockSl === null ||
                    riskForm.profitLockSl === "" ||
                    riskForm.profitLockSl === 0
                  }
                />
              </div>
              <div className="risk-modal-input-container">
                <label className="risk-modal-label">Trail Profit by Y</label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <PlusMinQty
                    qtyClick={(qty) => {
                      setRiskForm((prev) => ({
                        ...prev,
                        profitLockTrail: { ...prev.profitLockTrail, step: qty },
                      }));
                      setTimeout(() => {
                        validateRiskProfitLockTrail(
                          riskForm.profitLockTrail.reference,
                          qty
                        );
                      }, 0);
                    }}
                    filterValue={riskForm.profitLockTrail.step || null}
                    name={"profitLockTrailStep"}
                    min={0.05}
                    step={0.05}
                    max={null}
                    title={""}
                    formatINR={true}
                    disabled={
                      riskForm.profitLockAt === null ||
                      riskForm.profitLockAt === "" ||
                      riskForm.profitLockAt === 0 ||
                      riskForm.profitLockSl === null ||
                      riskForm.profitLockSl === "" ||
                      riskForm.profitLockSl === 0
                    }
                  />
                  <div className="pts-class risk-modal-pts-border">
                    {renderPNLLabel()}
                  </div>
                </div>
              </div>
            </div>
            {riskErrorMessages.profitLockTrail && (
              <div
                className="text-danger mt-1 mb-2"
                style={{ fontSize: "10px", textAlign: "center" }}
              >
                {riskErrorMessages.profitLockTrail}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StrategyLegs;